import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface StoreChoiceModalProps {
  open: boolean;
  onClose: () => void;
}

export default function StoreChoiceModal({ open, onClose }: StoreChoiceModalProps) {
  const { t } = useTranslation();

  const handleEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, handleEsc]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-md p-8 shadow-lg animate-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-all cursor-pointer"
          aria-label="Fermer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Icon */}
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-5">
          <i className="ri-smartphone-line text-2xl"></i>
        </div>

        {/* Title */}
        <h3 className="font-heading text-xl font-bold text-foreground-900 text-center mb-2">
          {t('storeModal.title')}
        </h3>
        <p className="text-sm text-foreground-600 text-center mb-8">
          {t('storeModal.subtitle')}
        </p>

        {/* Store Buttons */}
        <div className="flex flex-col gap-3">
          {/* Google Play */}
          <a
            href="https://play.google.com/store/apps/details?id=com.sharvayaInfotech.CourtBooking"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 px-5 py-4 rounded-xl bg-foreground-950 text-background-50 hover:bg-foreground-900 transition-all duration-300 cursor-pointer group"
          >
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <i className="ri-google-play-fill text-2xl"></i>
            </div>
            <div className="text-left">
              <p className="text-xs text-background-50/60 uppercase tracking-wide">{t('storeModal.getItOn')}</p>
              <p className="text-base font-semibold">{t('storeModal.googlePlay')}</p>
            </div>
            <div className="w-5 h-5 flex items-center justify-center ml-auto">
              <i className="ri-arrow-right-line text-background-50/60 group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </a>

          {/* App Store */}
          <a
            href="https://apps.apple.com/in/app/tennis-club-de-douala/id6752776130"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 px-5 py-4 rounded-xl bg-foreground-950 text-background-50 hover:bg-foreground-900 transition-all duration-300 cursor-pointer group"
          >
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <i className="ri-app-store-fill text-2xl"></i>
            </div>
            <div className="text-left">
              <p className="text-xs text-background-50/60 uppercase tracking-wide">{t('storeModal.downloadOn')}</p>
              <p className="text-base font-semibold">{t('storeModal.appStore')}</p>
            </div>
            <div className="w-5 h-5 flex items-center justify-center ml-auto">
              <i className="ri-arrow-right-line text-background-50/60 group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}